import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import SendIcon from "@mui/icons-material/Send";
import { Divider, IconButton, InputBase, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useWindowTile } from "desktopWindow/context/WindowTilesContext";
import * as React from "react";
import { isAndroid, isDesktop, isIOS, isMobile } from "react-device-detect";
import { useIntl } from "react-intl";
import { useGameRoom } from "../../context/GameContext";
import { chatBorderColor } from "../types";

const useChatInputStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		width: "100%",
		minHeight: isMobile ? 0 : 50,
		borderRadius: 4,
		border: `1px solid ${chatBorderColor}`,
	},
	input: {
		flex: 1,
		width: "100%",
		fontSize: "0.875rem",
	},
}));
export const ChatInput: React.FC<{
	onSend: (text: string) => void;
	setToggleScroll: (value: boolean) => void;
	toggleScroll: boolean;
}> = ({ onSend, toggleScroll, setToggleScroll }) => {
	const classes = useChatInputStyles();
	const { MobileWindowResized } = useWindowTile("chat");
	const intl = useIntl();
	const { chatBotConfig } = useGameRoom();
	const inputRef = React.useRef<HTMLInputElement>();
	const [sendDisabled, setSendDisabled] = React.useState(true);

	const getTextInputValue = () => {
		return inputRef.current?.value.trim();
	};
	const sendMessage = () => {
		const text = getTextInputValue();
		if (!text) {
			return;
		}
		onSend(text);
		setSendDisabled(true);

		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const onKeyPress = (e: React.KeyboardEvent) => {
		const isEnter = e.key === "Enter";
		const isShiftEnter = isEnter && e.shiftKey;
		if (isShiftEnter) {
			return;
		}
		if (isEnter) {
			e.preventDefault();
			sendMessage();
			setSendDisabled(true);
			return;
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const text = e.currentTarget.value;
		const textLength = text?.length || 0;

		if (textLength > 0 && sendDisabled) {
			setSendDisabled(false);
		} else if (textLength === 0 && !sendDisabled) {
			setSendDisabled(true);
		}
	};

	const resizeToFullScreen = () => {
		if (isAndroid) {
			window.setTimeout(() => {
				MobileWindowResized("chat", true);
			}, 250);
		} else if (isIOS) {
			MobileWindowResized("chat", true);
		}
	};
	const restoreChatWindowSize = () => {
		window.setTimeout(() => {
			MobileWindowResized("chat", false);
		}, 150);
		window.setTimeout(() => {
			setToggleScroll(!toggleScroll);
		}, 250);
	};

	return (
		<Paper elevation={0} className={classes.root}>
			<div
				style={{
					padding: "4px 20px",
					display: isMobile ? "flex" : "block",
					justifyContent: "space-between",
				}}
			>
				<InputBase
					multiline
					maxRows={4}
					// Message ${chatBotConfig.channelName}
					placeholder={intl.formatMessage(
						{ id: "CHAT.input.placeholder" },
						{ name: chatBotConfig.channelName },
					)}
					inputProps={{ "data-testid": "chat-textarea" }}
					inputRef={inputRef}
					className={classes.input}
					onKeyPress={onKeyPress}
					onChange={onChange}
					onFocus={() => isMobile && resizeToFullScreen}
					onBlur={() => isMobile && restoreChatWindowSize}
				/>
				{isMobile && (
					<IconButton style={{ color: sendDisabled ? "grey" : "black" }} size="small" onClick={sendMessage}>
						<SendIcon fontSize="small" />
					</IconButton>
				)}
			</div>
			{isDesktop && (
				<>
					<Divider />
					<div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
						<IconButton size="small">
							<FormatBoldIcon fontSize="small" />
						</IconButton>
						<IconButton size="small">
							<FormatItalicIcon fontSize="small" />
						</IconButton>
						<IconButton size="small">
							<FormatUnderlinedIcon fontSize="small" />
						</IconButton>
						<div style={{ flexGrow: 1 }} />
						<IconButton size="small">
							<InsertEmoticonIcon fontSize="small" />
						</IconButton>
						<IconButton color={sendDisabled ? "default" : "secondary"} size="small" onClick={sendMessage}>
							<SendIcon fontSize="small" />
						</IconButton>
					</div>
				</>
			)}
		</Paper>
	);
};
